import { getAllProgramas, programasVigentes } from '@/data/programasVigentes';
import { supabase } from '@/lib/supabase';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

interface RegisterForm {
  email: string;
  password: string;
  confirmPassword: string;
  name?: string;
  customerType: 'egresado_unach' | 'publico_general';
  graduateProgram?: string;
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterForm>();

  const password = watch('password');
  const customerType = watch('customerType');
  const isEgresadoUnach = customerType === 'egresado_unach';

  // Estado para el filtro de búsqueda de programas
  const [programaFilter, setProgramaFilter] = useState('');

  // Obtener lista plana de programas (para conteo)
  const allProgramas = useMemo(() => getAllProgramas(), []);

  // Función para normalizar texto: quitar acentos y convertir a minúsculas
  const normalizeText = (text: string): string => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, ''); // Elimina diacríticos (acentos)
  };

  // Filtrar programas por unidad según búsqueda
  const programasPorUnidad = useMemo(() => {
    const filtroTrimmed = programaFilter.trim();
    const hayFiltroLocal = filtroTrimmed.length > 0;
    const filterNormalized = normalizeText(filtroTrimmed);

    const resultado: { nombreUnidad: string; municipio: string; programas: string[] }[] = [];

    for (const unidad of programasVigentes) {
      if (hayFiltroLocal) {
        // Buscar en nombre de unidad o municipio (sin importar acentos ni mayúsculas)
        const unidadCoincide =
          normalizeText(unidad.nombreUnidad).includes(filterNormalized) ||
          normalizeText(unidad.municipio).includes(filterNormalized);
        const programasFiltrados = unidadCoincide
          ? unidad.programas // Si la unidad/municipio coincide, mostrar todos sus programas
          : unidad.programas.filter((p) => normalizeText(p).includes(filterNormalized));

        if (programasFiltrados.length === 0) continue;

        resultado.push({
          nombreUnidad: unidad.nombreUnidad,
          municipio: unidad.municipio,
          programas: [...programasFiltrados].sort((a, b) => a.localeCompare(b, 'es')),
        });
      } else {
        // Sin filtro, mostrar todos
        resultado.push({
          nombreUnidad: unidad.nombreUnidad,
          municipio: unidad.municipio,
          programas: [...unidad.programas].sort((a, b) => a.localeCompare(b, 'es')),
        });
      }
    }

    return resultado.sort((a, b) => a.nombreUnidad.localeCompare(b.nombreUnidad, 'es'));
  }, [programaFilter]);

  const hayFiltro = programaFilter.trim().length > 0;

  // Contar programas mostrados
  const programasMostrados = programasPorUnidad.reduce((acc, u) => acc + u.programas.length, 0);

  const onSubmit = async (data: RegisterForm) => {
    setIsLoading(true);
    setError('');

    try {
      // Registrar usuario en Supabase Auth (rol client se asigna automáticamente por trigger)
      const { error: authError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            name: data.name || null,
            customer_type: data.customerType,
            graduate_program: data.customerType === 'egresado_unach' ? data.graduateProgram : null,
          },
        },
      });

      if (authError) throw authError;

      // Redirigir a login
      setTimeout(() => {
        navigate('/login', {
          state: { message: 'Cuenta creada exitosamente. Por favor inicia sesión.' },
        });
      }, 500);
    } catch (err) {
      // No logueamos como error porque fallos de registro (email duplicado, etc.) son flujos esperados
      setError(err instanceof Error ? err.message : 'Error al registrarse');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="card">
          <h1 className="text-3xl font-bold text-center mb-6">Crear Cuenta de Cliente</h1>
          <p className="text-center text-gray-600 mb-6 text-sm">
            Regístrate para comprar tickets y ver tus compras
          </p>

          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Email *</label>
              <input
                type="email"
                className="input-field"
                {...register('email', {
                  required: 'El email es requerido',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Email inválido',
                  },
                })}
              />
              {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Nombre (opcional)</label>
              <input
                type="text"
                className="input-field"
                {...register('name')}
                placeholder="Tu nombre completo"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Tipo de Cliente *</label>
              <select
                className="input-field"
                {...register('customerType', {
                  required: 'Debes seleccionar un tipo de cliente',
                })}
              >
                <option value="">Selecciona una opción</option>
                <option value="egresado_unach">Egresado UNACH</option>
                <option value="publico_general">Público en General</option>
              </select>
              {errors.customerType && (
                <p className="text-red-600 text-sm mt-1">{errors.customerType.message}</p>
              )}
            </div>

            {/* Programa Educativo (solo para Egresados UNACH) */}
            {isEgresadoUnach && (
              <div>
                <label className="block text-sm font-medium mb-1">Programa Educativo *</label>

                {/* Campo de búsqueda */}
                <input
                  type="text"
                  placeholder="Buscar programa o facultad..."
                  value={programaFilter}
                  onChange={(e) => setProgramaFilter(e.target.value)}
                  className="input-field mb-2"
                  disabled={isLoading}
                />

                <select
                  {...register('graduateProgram', {
                    required: isEgresadoUnach ? 'Debes seleccionar tu programa educativo' : false,
                  })}
                  className={`input-field ${errors.graduateProgram ? 'border-red-500' : ''}`}
                  disabled={isLoading}
                >
                  <option value="">Selecciona tu programa educativo</option>
                  {programasPorUnidad.map((unidad) => (
                    <optgroup key={unidad.nombreUnidad} label={unidad.nombreUnidad}>
                      {unidad.programas.map((programa, index) => (
                        <option key={`${unidad.nombreUnidad}-${programa}-${index}`} value={programa}>
                          {programa}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {errors.graduateProgram && (
                  <p className="text-red-600 text-sm mt-1">{errors.graduateProgram.message}</p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  Selecciona el programa del cual egresaste de la UNACH
                </p>
                {hayFiltro && (
                  <p className="mt-1 text-xs text-blue-600">
                    Mostrando {programasMostrados} de {allProgramas.length} programas
                  </p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1">Contraseña</label>
              <input
                type="password"
                className="input-field"
                {...register('password', {
                  required: 'La contraseña es requerida',
                  minLength: { value: 8, message: 'Mínimo 8 caracteres' },
                })}
              />
              {errors.password && (
                <p className="text-red-600 text-sm mt-1">{errors.password.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Confirmar Contraseña</label>
              <input
                type="password"
                className="input-field"
                {...register('confirmPassword', {
                  required: 'Debes confirmar la contraseña',
                  validate: (value) => value === password || 'Las contraseñas no coinciden',
                })}
              />
              {errors.confirmPassword && (
                <p className="text-red-600 text-sm mt-1">{errors.confirmPassword.message}</p>
              )}
            </div>

            <button type="submit" className="btn-primary w-full" disabled={isLoading}>
              {isLoading ? 'Creando cuenta...' : 'Crear Cuenta'}
            </button>
          </form>

          <p className="text-center mt-4 text-sm text-gray-600">
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" className="text-blue-600 hover:underline">
              Inicia sesión aquí
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
